import { cn } from '@woonstad/helpers-utils';
import clsx from 'clsx';
import { ReactElement } from 'react';

import { usesNewsTypeColors } from '../../news-type-classes';
import { Typography } from '../typography/Typography';

type RelatedProps = {
  children: ReactElement<any>;
  publishDate?: string;
  type?: string;
  typeClasses?: string;
  title?: string;
  intro?: string;
}

export const RelatedCard = ({
  children,
  type,
  typeClasses,
  title,
  intro,
  publishDate,
}: RelatedProps) => {
  const isKnownNewsType = type === 'Blog' || type === 'Evenement' || type === 'Hoe werkt het' || usesNewsTypeColors(type);

  const defaultTypeClasses = clsx(
    'w-max',
    {
      'text-news-type-blog': type === 'Blog',
      'text-news-type-event': type === 'Evenement',
      'text-news-type-howto': type === 'Hoe werkt het',
      'text-news-type-news': usesNewsTypeColors(type),
    },
  );

  const typeBorderClasses = clsx(
    'before:absolute before:left-0 before:top-0 before:h-full before:w-px before:bg-[#E0E8F6] before:transition-all before:content-[""] group-hover:before:w-[3px]',
    !isKnownNewsType && 'group-hover:before:bg-news-type-news',
    {
      'group-hover:before:bg-news-type-blog': type === 'Blog',
      'group-hover:before:bg-news-type-event': type === 'Evenement',
      'group-hover:before:bg-news-type-howto': type === 'Hoe werkt het',
      'group-hover:before:bg-news-type-news': usesNewsTypeColors(type),
    },
  );

  const titleClasses = clsx(
    'line-clamp-3 break-words text-news-title underline-offset-4 group-hover:underline',
    !isKnownNewsType && 'group-hover:text-news-type-news-title-hover',
    {
      'group-hover:text-news-type-blog-title-hover': type === 'Blog',
      'group-hover:text-news-type-event-title-hover': type === 'Evenement',
      'group-hover:text-news-type-howto-title-hover': type === 'Hoe werkt het',
      'group-hover:text-news-type-news-title-hover': usesNewsTypeColors(type),
    },
  );

  return (
    <children.type
      {...(children.props as any)}
      aria-label={`${title} | ${type}`}
      className={cn('group block h-full', (children.props as any).className)}
    >
      <div
        className={clsx(
          'flex h-full py-4',
        )}
      >
        <div
          className={clsx(
            'relative flex h-full flex-col justify-center space-y-2',
            typeBorderClasses,
          )}
        >
          <div
            className={cn(
              'bg-news px-4',
            )}
          >
            <Typography
              variant="title-body"
              className={titleClasses}
            >
              {title}
            </Typography>

          </div>

          <div
            className={cn(
              'inline-block w-fit bg-news pl-4',
            )}
          >
            <div className="flex flex-wrap gap-x-2 gap-y-1 pb-2">
              {publishDate ? (
                <>
                  <Typography
                    variant="label-small"
                    type="span"
                    className="w-max text-news-date"
                  >
                    {publishDate}
                  </Typography>
                  {type ? (
                    <Typography
                      variant="label-small"
                      type="span"
                      className="text-news-date"
                    >
                      •
                    </Typography>
                  ) : null}
                </>
              ) : null}

              <Typography
                variant="label-small"
                type="span"
                className={cn(
                  defaultTypeClasses,
                  typeClasses,
                )}
              >
                {type}
              </Typography>
            </div>

            <Typography
              variant="content-body"
              textColorClassname="text-news-intro"
              className="line-clamp-2"
            >
              {intro}
            </Typography>
          </div>
        </div>
      </div>

    </children.type>
  );
};
